// TODO: Remove this once we can auth
import type { Dispatch, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { logError } from '~/common/utils/log';
import { useKYCStatus } from '~/hooks/useKYCStatus';
import { SocureKycStatus } from '~/services/users/types';

import { UserContext } from './UserProvider';

export type KYCStatusRead = { status: SocureKycStatus | undefined; isError: boolean };

export const KYCContext = createContext<{
  KYCStatus: SocureKycStatus | null;
  setKYCStatus: Dispatch<SetStateAction<SocureKycStatus | null>>;
  // True only after the user completes a verification step in THIS browser session
  // (a genuine ACCEPT from the DocV poll or the KYC form) — NOT for an ambient
  // `/verification` ACCEPT (mount / window-refocus refetch). Gates the return-to-origin
  // redirect so a user is never bounced back before actually verifying (EDGE-32717).
  // Held in React state, so it resets on a full page load: it survives the client-side
  // refer→index hop but not the IDComply full-page round-trip — which is the intent.
  didCompleteVerificationThisSession: boolean;
  // Set the status AND mark an in-session completion when it is a genuine ACCEPT. Call
  // this (instead of setKYCStatus) from the genuine verification-result paths.
  completeVerification: (status: SocureKycStatus) => void;
  isKYCloading: boolean;
  isKYCfetching: boolean;
  isKYCError: boolean;
  refetchKYCStatus: () => Promise<KYCStatusRead>;
}>({
  KYCStatus: null,
  setKYCStatus: null,
  didCompleteVerificationThisSession: false,
  completeVerification: () => {},
  isKYCloading: false,
  isKYCfetching: false,
  isKYCError: false,
  refetchKYCStatus: async () => ({ status: undefined, isError: false }),
});

interface KYCProviderProps {
  children: React.ReactNode;
}

function KYCProvider({ children }: KYCProviderProps) {
  const [KYCStatus, setKYCStatus] = useState<SocureKycStatus | null>(null);
  const [didCompleteVerificationThisSession, setDidCompleteVerification] = useState(false);
  const { user } = useContext(UserContext);

  // Called from the genuine verification-result paths (DocV poll, KYC form). Marks an
  // in-session completion only on a real ACCEPT — the ambient `/verification` query keeps
  // using setKYCStatus directly and therefore never flips this flag.
  const completeVerification = useCallback((status: SocureKycStatus) => {
    setKYCStatus(status);
    if (status === SocureKycStatus.ACCEPT) {
      setDidCompleteVerification(true);
    }
  }, []);

  const {
    data: kycStatus,
    isInitialLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useKYCStatus(user?.id);

  useEffect(() => {
    if (kycStatus) {
      setKYCStatus(kycStatus);
    }

    if (error) {
      logError(error);
    }
  }, [kycStatus, error]);

  const value = useMemo(
    () => ({
      KYCStatus,
      setKYCStatus,
      didCompleteVerificationThisSession,
      completeVerification,
      isKYCloading: isInitialLoading,
      isKYCfetching: isFetching,
      isKYCError: isError,
      refetchKYCStatus: async () => {
        const { data, isError: isReadError } = await refetch({ cancelRefetch: false });
        return { status: data, isError: isReadError };
      },
    }),
    [
      KYCStatus,
      didCompleteVerificationThisSession,
      completeVerification,
      isInitialLoading,
      isFetching,
      isError,
      refetch,
    ]
  );

  return <KYCContext.Provider value={value}>{children}</KYCContext.Provider>;
}

export default KYCProvider;
